import { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import { X, Plus, Check, FileText, Gamepad2, Gift, Crown, Globe } from 'lucide-react';
import { ModalShell, ModalHeader, type Plan } from '../server/ServerModals';
import Flag from 'react-world-flags';

// =========================================================================
// Location helpers
// =========================================================================

const LOCATION_INFO: Record<string, { name: string; description: string; flag: string }> = {
  nbg1: { name: 'Alemania', description: 'Núremberg, Europa Central', flag: 'DE' },
  ash:  { name: 'Estados Unidos', description: 'Ashburn, VA · Costa Este', flag: 'US' },
  fsn1: { name: 'Alemania', description: 'Falkenstein, Europa Central', flag: 'DE' },
  hel1: { name: 'Finlandia', description: 'Helsinki, Europa del Norte', flag: 'FI' },
  hil:  { name: 'Estados Unidos', description: 'Hillsboro, OR · Costa Oeste', flag: 'US' },
};

function getLocationInfo(code?: string) {
  if (!code) return { name: code ?? '—', description: '', flag: '' };
  return LOCATION_INFO[code] || { name: code.toUpperCase(), description: code, flag: '' };
}

function LocationFlag({ code }: { code: string }) {
  if (!code) return <Globe size={14} />;
  return (
    <Flag
      code={code}
      style={{ width: '1em', height: '1em', verticalAlign: '-0.125em', borderRadius: '2px' }}
    />
  );
}

// =========================================================================
// Shared types
// =========================================================================

export interface DashboardServer {
  id: string;
  name: string;
  type: string;
  version: string;
  status: 'online' | 'offline' | 'maintenance';
  active?: boolean;
  serverStartTime?: string;
  permissions?: string;
}

export interface GameVersion {
  id: string;
  name: string;
  popular: boolean;
}

export interface GameVersions {
  [game: string]: {
    [type: string]: GameVersion[];
  };
}

export interface CreateFormData {
  name: string;
  game: string;
  type: string;
  version: string;
  maxPlayers: number;
  plan_id?: string;
}

// =========================================================================
// TosModal
// =========================================================================

interface TosModalProps {
  appName: string;
  accepted: boolean;
  accepting: boolean;
  onAccept: () => void;
  onToggle: (v: boolean) => void;
}

export function TosModal({ appName, accepted, accepting, onAccept, onToggle }: TosModalProps) {
  return (
    <ModalShell onClose={() => { }} size="sm">
      <ModalHeader
        icon={<FileText size={18} />}
        title="Términos de servicio"
        subtitle={`Para usar ${appName}`}
        onClose={() => { }}
      />
      <div className="sp-modal-body">
        <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '1.25rem' }}>
          Para usar {appName}, debes leer y aceptar nuestros{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>
            Términos del Servicio
          </a>.
        </p>
        <label className="sp-toggle" style={{ marginBottom: 0 }}>
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => onToggle(e.target.checked)}
          />
          <span className="sp-toggle-track"><span className="sp-toggle-thumb" /></span>
          <span className="sp-toggle-label">He leído y acepto los Términos del Servicio.</span>
        </label>
      </div>
      <div className="sp-modal-footer">
        <button
          type="button"
          className="btn btn-primary"
          onClick={onAccept}
          disabled={!accepted || accepting}
        >
          {accepting ? <><span className="spinner-small" /> Aceptando…</> : <><Check size={14} /> Aceptar y continuar</>}
        </button>
      </div>
    </ModalShell>
  );
}

// =========================================================================
// CreateServerModal
// =========================================================================

interface CreateServerModalProps {
  appName: string;
  formData: CreateFormData;
  availableGames: string[];
  availableTypes: string[];
  versions: GameVersions | null;
  selectedGame: string;
  maxPlayersDefault: number;
  plans: Plan[];
  loadingPlans: boolean;
  selectedPlan: Plan | null;
  error: string | null;
  creating: boolean;
  onChangeName: (v: string) => void;
  onSelectGame: (game: string) => void;
  onSelectType: (type: string) => void;
  onSelectVersion: (v: string) => void;
  onSelectPlan: (plan: Plan) => void;
  onSubmit: (e: FormEvent) => void;
  onClose: () => void;
}

function formatTypeServer(type: string): string {
  let name = type.charAt(0).toUpperCase() + type.slice(1);
  name = name.replace('Terraria-vanilla', 'Terraria');
  return name;
}

export function CreateServerModal({
  formData,
  availableGames,
  availableTypes,
  versions,
  selectedGame,
  maxPlayersDefault,
  plans,
  loadingPlans,
  selectedPlan,
  error,
  creating,
  onChangeName,
  onSelectGame,
  onSelectType,
  onSelectVersion,
  onSelectPlan,
  onSubmit,
  onClose,
}: CreateServerModalProps) {
  const gameVersions = versions?.[selectedGame]?.[formData.type] || [];
  const [activeLocation, setActiveLocation] = useState<string | null>(null);

  const availableLocations = Array.from(
    new Set(plans.map((p) => p.location).filter((l): l is string => Boolean(l)))
  );

  useEffect(() => {
    if (activeLocation || plans.length === 0) return;
    if (selectedPlan?.location) {
      setActiveLocation(selectedPlan.location);
      return;
    }
    if (availableLocations.length > 0) setActiveLocation(availableLocations[0]);
  }, [plans, selectedPlan, activeLocation]);

  const filteredPlans = activeLocation
    ? plans.filter((p) => p.location === activeLocation)
    : [];

  return (
    <ModalShell onClose={onClose} size="md">
      <ModalHeader
        icon={<Gamepad2 size={18} />}
        title="Crear servidor"
        subtitle="Configura tu nuevo servidor de juego"
        onClose={onClose}
      />
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <div className="sp-modal-body">
          {error && (
            <div className="sp-banner sp-banner--error" style={{ borderRadius: 4, borderBottom: 'none', marginBottom: '1rem' }}>
              <span className="sp-banner-text">{error}</span>
            </div>
          )}

          <div className="sp-form-grid">
            <div className="sp-form-field">
              <label htmlFor="create-game">Juego</label>
              <select
                id="create-game"
                value={selectedGame}
                onChange={(e) => onSelectGame(e.target.value)}
                required
              >
                {availableGames.map((game) => (
                  <option key={game} value={game}>{game}</option>
                ))}
              </select>
            </div>

            <div className="sp-form-field">
              <label htmlFor="create-name">Nombre</label>
              <input
                id="create-name"
                type="text"
                value={formData.name}
                onChange={(e) => onChangeName(e.target.value)}
                placeholder="Mi servidor"
                required
                minLength={1}
                maxLength={50}
              />
            </div>
          </div>

          <div className="sp-form-field" style={{ marginTop: '1rem' }}>
            <label>Software</label>
            <div className="sp-type-buttons">
              {availableTypes.map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`sp-type-btn ${formData.type === type ? 'is-active' : ''}`}
                  onClick={() => onSelectType(type)}
                >
                  {formatTypeServer(type)}
                </button>
              ))}
            </div>
          </div>

          <div className="sp-form-field" style={{ marginTop: '1rem' }}>
            <label htmlFor="create-version">Versión</label>
            <select
              id="create-version"
              value={formData.version}
              onChange={(e) => onSelectVersion(e.target.value)}
              required
            >
              {gameVersions.map((version) => (
                <option key={version.id} value={version.id}>
                  {version.name} {version.popular ? '⭐' : ''}
                </option>
              ))}
            </select>
          </div>

          <input type="hidden" value={formData.maxPlayers || maxPlayersDefault} />

          <div className="sp-form-field" style={{ marginTop: '1rem' }}>
            <label htmlFor="create-plan">Elige un plan</label>
            {loadingPlans ? (
              <div className="sp-modal-body--center" style={{ textAlign: 'center', padding: '1rem 0' }}>
                <div className="spinner" />
                <p style={{ marginTop: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  Cargando planes…
                </p>
              </div>
            ) : plans.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                No hay planes disponibles para este juego.
              </p>
            ) : (
              <>
                {availableLocations.length > 1 && (
                  <div className="sp-location-tabs" role="tablist" aria-label="Ubicaciones" style={{ marginBottom: '0.75rem' }}>
                    {availableLocations.map((locCode) => {
                      const info = getLocationInfo(locCode);
                      const isActive = locCode === activeLocation;
                      return (
                        <button
                          key={locCode}
                          type="button"
                          role="tab"
                          aria-selected={isActive}
                          className={`sp-location-tab ${isActive ? 'is-active' : ''}`}
                          onClick={() => setActiveLocation(locCode)}
                        >
                          <span className="sp-location-tab-flag" aria-hidden="true"><LocationFlag code={info.flag} /></span>
                          <span className="sp-location-tab-name">{info.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                {filteredPlans.length === 0 ? (
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    No hay planes disponibles para esta ubicación.
                  </p>
                ) : (
                  <div className="sp-plan-grid">
                    {filteredPlans.map((plan) => {
                      const isSelected = selectedPlan?.id === plan.id;
                      return (
                        <button
                          key={plan.id}
                          type="button"
                          className={`sp-plan-card ${isSelected ? 'is-current' : ''}`}
                          onClick={() => onSelectPlan(plan)}
                        >
                          <div className="sp-plan-card-head">
                            <span className="sp-plan-card-label">{plan.label}</span>
                            {isSelected && <span className="sp-plan-card-current">seleccionado</span>}
                          </div>
                          <p className="sp-plan-card-desc">{plan.description}</p>
                          <div className="sp-plan-card-price">
                            <span>${plan.price.toFixed(3)}</span>
                            <span className="sp-plan-card-unit">/hora</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="sp-modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={creating}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={creating}>
            {creating ? <><span className="spinner-small" /> Creando…</> : <><Plus size={14} /> Crear servidor</>}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// =========================================================================
// ReferralModal
// =========================================================================

interface ReferralModalProps {
  appName: string;
  code: string;
  error: string;
  onChangeCode: (v: string) => void;
  onSubmit: (wasInvited: boolean) => void;
}

export function ReferralModal({ appName, code, error, onChangeCode, onSubmit }: ReferralModalProps) {
  return (
    <ModalShell onClose={() => { }} size="sm">
      <ModalHeader
        icon={<Gift size={18} />}
        title="¿Te invitaron?"
        subtitle="Si alguien te invitó, ambos pueden recibir beneficios"
        onClose={() => { }}
      />
      <div className="sp-modal-body">
        <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '1.25rem' }}>
          ¿Alguien te invitó a {appName}? Si es así, introduce su código para que ambos reciban créditos.
        </p>

        {error && (
          <div className="sp-banner sp-banner--error" style={{ borderRadius: 4, borderBottom: 'none', marginBottom: '1rem' }}>
            <span className="sp-banner-text">{error}</span>
          </div>
        )}

        <div className="sp-form-field">
          <label htmlFor="referralCode">Código de referido (opcional)</label>
          <input
            id="referralCode"
            type="text"
            value={code}
            onChange={(e) => onChangeCode(e.target.value.toUpperCase().slice(0, 6))}
            placeholder="Código de 6 caracteres"
            maxLength={6}
            style={{ fontFamily: 'var(--font-mono, monospace)', textTransform: 'uppercase', letterSpacing: '0.1em' }}
          />
        </div>
      </div>
      <div className="sp-modal-footer">
        <button type="button" className="btn btn-ghost" onClick={() => onSubmit(false)}>
          No, nadie me invitó
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => onSubmit(true)}
          disabled={!code.trim()}
        >
          Sí, tengo código
        </button>
      </div>
    </ModalShell>
  );
}

// =========================================================================
// ReferralSuccessModal
// =========================================================================

interface ReferralSuccessModalProps {
  appName: string;
  referrerName: string;
  onClose: () => void;
}

export function ReferralSuccessModal({ appName, referrerName, onClose }: ReferralSuccessModalProps) {
  return (
    <ModalShell onClose={onClose} size="sm">
      <ModalHeader
        icon={<Gift size={18} />}
        title="¡Referido registrado!"
        subtitle={`Bienvenido a ${appName}`}
        onClose={onClose}
      />
      <div className="sp-modal-body" style={{ textAlign: 'center', padding: '1.5rem 1rem' }}>
        <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '0.75rem' }}>
          Te has registrado exitosamente. Fuiste referido por
        </p>
        <p style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--accent)' }}>
          {referrerName}
        </p>
      </div>
      <div className="sp-modal-footer">
        <button type="button" className="btn btn-primary" onClick={onClose}>
          <Check size={14} /> Continuar
        </button>
      </div>
    </ModalShell>
  );
}
